import { Box, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { NEUTRAL } from "../../theme/tokens";
import { ManagementCenterMultiHeader } from "./Header/ManagementCenterMultiHeader";
import { useManagementCenterMultiPpd } from "./hooks/useManagementCenterMulti";
import { ALL_CHARTS, type ManagementCenterChartKey } from "./utlis";

export const DEFAULT_CHARTS: ManagementCenterChartKey[] = ["ppdComparison", "hppdComparison", "otUse", "agencyUse"] as const;

export const ManagementCenterMulti = () => {
	const theme = useTheme();
	const { timezone } = useDepartmentFlags();
	const isLarge = useMediaQuery(theme.breakpoints.up("xl"));
	const navigate = useNavigate();
	const [visibleCharts, setVisibleCharts] = useState<ManagementCenterChartKey[]>(DEFAULT_CHARTS);
	const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
	const [timePeriod, setTimePeriod] = useState<number>(30);
	const [selectedDateFrame, setSelectedDateFrame] = useState<DateTime>(DateTime.now().setZone(timezone).minus({ days: timePeriod }));
	const [selectedEndDate, setSelectedEndDate] = useState<DateTime>(DateTime.now().setZone(timezone));

	const { data: ppdData } = useManagementCenterMultiPpd({ startDate: selectedDateFrame, endDate: selectedEndDate });

	const onStartDateChange = useCallback((date: DateTime) => {
		setSelectedDateFrame(date);
	}, []);

	const onEndDateChange = useCallback(
		(date?: DateTime) => {
			setSelectedEndDate(date ?? DateTime.now().setZone(timezone));
		},
		[timezone],
	);

	const availableRegions = useMemo(
		() => Array.from(new Set((ppdData?.items ?? []).map((item) => item.region).filter((r): r is string => Boolean(r)))),
		[ppdData],
	);
	const handleItemDoubleClick = useCallback(
		(departmentName: string) => {
			navigate(`/management-center?department=${encodeURIComponent(departmentName)}`);
		},
		[navigate],
	);

	const { chartsToRender, adjustedMaxRows } = useMemo(() => {
		const visibleSet = new Set<ManagementCenterChartKey>(visibleCharts);
		const filteredCharts = ALL_CHARTS.filter((chart) => visibleSet.has(chart.id));

		const scaledMaxRows = isLarge ? 6 : visibleCharts.length <= 3 ? 8 : 4;

		return {
			chartsToRender: filteredCharts,
			adjustedMaxRows: scaledMaxRows,
		};
	}, [visibleCharts, isLarge]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				bgcolor: alpha(NEUTRAL[200], 0.3),
				padding: 2,
				gap: 1,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<ManagementCenterMultiHeader
				visibleCharts={visibleCharts}
				onVisibleChartsChange={setVisibleCharts}
				availableRegions={availableRegions}
				selectedRegions={selectedRegions}
				onSelectedRegionsChange={setSelectedRegions}
				onTimePeriodChange={setTimePeriod}
				onStartDateChange={onStartDateChange}
				onEndDateChange={onEndDateChange}
			/>
			<Box
				sx={{
					display: "flex",
					width: "100%",
					flexWrap: "wrap",
					rowGap: 1,
					justifyContent: "space-between",
				}}
			>
				{(() => {
					const count = chartsToRender.length;
					if (count === 0) return null;

					// If 3 or fewer charts, keep them in a single row
					if (count <= 3) {
						const width = `calc(${100 / count}% - 4px)`;
						return chartsToRender.map((chart) =>
							chart.render(adjustedMaxRows, width, selectedRegions, handleItemDoubleClick, selectedDateFrame, selectedEndDate),
						);
					}

					const firstRowCount = Math.floor(count / 2);
					const secondRowCount = count - firstRowCount;

					return chartsToRender.map((chart, index) => {
						const count = index < firstRowCount ? firstRowCount : secondRowCount;
						const width = `calc(${100 / count}% - 4px)`;

						return chart.render(adjustedMaxRows, width, selectedRegions, handleItemDoubleClick, selectedDateFrame, selectedEndDate);
					});
				})()}
			</Box>
		</Box>
	);
};
